import dayjs, { type Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';
import { getPeriodToSeconds, utcZeroTimeToStandardTime } from '@/common/utils/commonUtils';
import { UTC_TIME_FORMAT } from '@/common/components/molecules/timeController/constants';
import { formatTimeByInterval } from '@/common/utils/core/time/format';
import type { MetricV7RequestInterpolateType } from '@/api-client/data/model';
import { CHART_PASSING_VALUE, type TimeRangeInfo } from '@/common/utils';
import type { BulkMetricDataValues, MetricDataValues } from './metric.types';
import type { RequiredChartData } from '../types';

const INTERPOLATE_VALUE: Record<MetricV7RequestInterpolateType, number | null> = {
  Null: null,
  Unknown: null,
  Linear: CHART_PASSING_VALUE,
  Zero: 0,
};

const convertTime = (time: string | number | Dayjs, useUtcZeroTime: boolean) => {
  // TODO: 개선
  const dayjsTime: Dayjs =
    typeof time === 'string' || typeof time === 'number' ? dayjs(time) : time;
  return useUtcZeroTime
    ? dayjsTime
    : dayjs(utcZeroTimeToStandardTime(dayjsTime.format(UTC_TIME_FORMAT)));
};

const formatValue = (value: any, formatter?: (value: number) => number | null) => {
  if (isNil(value) || isNaN(+value)) {
    return null;
  }
  return formatter ? formatter(+value) : +value;
};

const calculateStartTime = (fromTime: string, interval: string, intervalToSecond: number) => {
  const startTime = formatTimeByInterval(fromTime, interval);
  return dayjs(fromTime).format(UTC_TIME_FORMAT) === startTime
    ? dayjs(startTime)
    : dayjs(startTime).add(intervalToSecond, 'second');
};

export const fillNullByInterval = (
  values: any[][],
  option?: {
    interval?: string;
    fromTime?: string;
    toTime?: string;
    useUtcZeroTime?: boolean;
    interpolateType?: MetricV7RequestInterpolateType;
    valueFormatter?: (value: any) => any;
  },
): MetricDataValues => {
  const {
    interval,
    fromTime: fromTimeUtc0,
    toTime: toTimeUtc0,
    useUtcZeroTime = false,
    valueFormatter,
    interpolateType = 'Null',
  } = option ?? {};

  const interpolateValue = INTERPOLATE_VALUE[interpolateType];

  const intervalToSecond = getPeriodToSeconds((interval ?? 'i').slice(1));
  if (!interval || intervalToSecond === -1 || !fromTimeUtc0 || !toTimeUtc0) {
    return values.reduce<MetricDataValues>(
      (acc, [time, value]) => {
        acc.label.push(convertTime(time, useUtcZeroTime));
        acc.value.push(formatValue(value, valueFormatter));
        return acc;
      },
      { label: [], value: [] },
    );
  }

  let index = 0;

  const fromTime = convertTime(fromTimeUtc0, useUtcZeroTime);
  const toTime = convertTime(toTimeUtc0, useUtcZeroTime);
  let tempTime = calculateStartTime(fromTime.format(UTC_TIME_FORMAT), interval, intervalToSecond);

  const newMetricData: MetricDataValues = { label: [], value: [] };

  while (tempTime.isBefore(toTime)) {
    const currentTime = values[index]?.[0];
    const currentDayjs = isNil(currentTime) ? null : convertTime(currentTime, useUtcZeroTime);

    if (!currentDayjs || currentDayjs.isAfter(tempTime)) {
      // 해당 슬롯에 데이터 없음 → null 채움
      newMetricData.label.push(tempTime);
      newMetricData.value.push(interpolateValue);
      tempTime = tempTime.add(intervalToSecond, 'second');
    } else if (currentDayjs.isSame(tempTime)) {
      // 정확히 일치 → 데이터 사용
      newMetricData.label.push(currentDayjs);
      newMetricData.value.push(formatValue(values[index][1], valueFormatter));
      tempTime = tempTime.add(intervalToSecond, 'second');
      index++;
    } else {
      // currentTime < tempTime: fromTime 비정렬로 인해 윈도우 이전 데이터 → skip
      index++;
    }
  }

  return newMetricData;
};

type NonGenericBulkMetricDataValues = { label: Dayjs[]; values: Record<string, (number | null)[]> };
export const bulkFillNullByInterval = <T extends Record<string, any>>(
  data: T[],
  timeField: string = 'collectTime',
  keys: string[] = [],
  option?: {
    interval?: string;
    fromTime?: string;
    toTime?: string;
    useUtcZeroTime?: boolean;
    interpolateType?: MetricV7RequestInterpolateType;
    valueFormatter?: (value: any) => any;
  },
): BulkMetricDataValues<T> => {
  const {
    interval,
    fromTime: fromTimeUtc0,
    toTime: toTimeUtc0,
    useUtcZeroTime = false,
    valueFormatter,
    interpolateType = 'Null',
  } = option ?? {};

  const interpolateValue = INTERPOLATE_VALUE[interpolateType];

  const intervalToSecond = getPeriodToSeconds((interval ?? 'i').slice(1));
  if (!interval || intervalToSecond === -1 || !fromTimeUtc0 || !toTimeUtc0) {
    return data.reduce<NonGenericBulkMetricDataValues>(
      (acc, cur) => {
        acc.label.push(convertTime(cur[timeField], useUtcZeroTime));
        keys.forEach((key) => {
          if (!acc.values[key]) {
            acc.values[key] = [];
          }
          acc.values[key].push(formatValue(cur[key], valueFormatter));
        });
        return acc;
      },
      { label: [], values: {} },
    ) as BulkMetricDataValues<T>;
  }

  let index = 0;

  const fromTime = convertTime(fromTimeUtc0, useUtcZeroTime);
  const toTime = convertTime(toTimeUtc0, useUtcZeroTime);
  let tempTime = calculateStartTime(fromTime.format(UTC_TIME_FORMAT), interval, intervalToSecond);

  const newMetricData: NonGenericBulkMetricDataValues = { label: [], values: {} };

  while (tempTime.isBefore(toTime)) {
    const currentTime = data[index]?.[timeField];
    const currentDayjs = isNil(currentTime) ? null : convertTime(currentTime, useUtcZeroTime);

    if (!currentDayjs || currentDayjs.isAfter(tempTime)) {
      // 해당 슬롯에 데이터 없음 → null 채움
      newMetricData.label.push(tempTime);
      keys.forEach((key) => {
        if (!newMetricData.values[key]) {
          newMetricData.values[key] = [];
        }
        newMetricData.values[key].push(interpolateValue);
      });
      tempTime = tempTime.add(intervalToSecond, 'second');
    } else if (currentDayjs.isSame(tempTime)) {
      newMetricData.label.push(currentDayjs);
      keys.forEach((key) => {
        if (!newMetricData.values[key]) {
          newMetricData.values[key] = [];
        }
        newMetricData.values[key].push(formatValue(data[index][key], valueFormatter));
      });
      index++;
      tempTime = tempTime.add(intervalToSecond, 'second');
    } else {
      // currentTime < tempTime: fromTime 비정렬로 인해 윈도우 이전 데이터 → skip
      index++;
    }
  }

  return newMetricData as BulkMetricDataValues<T>;
};

/**
 * 스캐터 차트 x축 시간 범위를 생성한다.
 * API 응답이 빈 배열인 경우에도 x축이 현재 시간 범위를 표시하도록 사용한다.
 *
 * @param fromTimeUtc0 - UTC+0 기준 시작 시간 문자열
 * @param toTimeUtc0 - UTC+0 기준 종료 시간 문자열
 * @returns xRange: 차트 axesX[0].range 에 설정할 [from, to] Dayjs 배열
 */
export const getScatterChartXAxisRange = (
  fromTimeUtc0: string,
  toTimeUtc0: string,
): {
  xRange: [Dayjs, Dayjs];
} => {
  const xRange: [Dayjs, Dayjs] = [
    dayjs(utcZeroTimeToStandardTime(fromTimeUtc0)),
    dayjs(utcZeroTimeToStandardTime(toTimeUtc0)),
  ];

  return { xRange };
};

export const getNullFilledDummyData = (
  timeRange?: Partial<TimeRangeInfo>,
  interval: string = 'I1m',
): RequiredChartData => {
  const nullFilledDummy = fillNullByInterval([], {
    interval,
    fromTime: timeRange?.fromTime,
    toTime: timeRange?.toTime,
  });

  return {
    series: { _null: { showLegend: false } },
    labels: nullFilledDummy.label,
    data: { _null: nullFilledDummy.value },
  };
};
